import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "../lib/reveal";
import { useLang } from "../i18n/LanguageContext";

const PROBLEM_IMG =
  "https://images.unsplash.com/photo-1674321576323-82be6b715ebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHx3YXN0ZSUyMG1hbmFnZW1lbnQlMjBsYW5kZmlsbHxlbnwwfHx8fDE3ODUyNDAxODR8MA&ixlib=rb-4.1.0&q=85";

const problems = [
  { en: "Unbearable landfill odour", hi: "असहनीय लैंडफिल गंध" },
  { en: "Slow decomposition of legacy waste", hi: "पुराने अपशिष्ट का धीमा अपघटन" },
  { en: "Methane & toxic gas emissions", hi: "मीथेन व विषैली गैस उत्सर्जन" },
  { en: "Leachate contamination", hi: "लीचेट संदूषण" },
  { en: "Worker health & citizen complaints", hi: "श्रमिक स्वास्थ्य व नागरिक शिकायतें" },
];

export const Problems = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section id="problem" data-testid="problem-section" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal className="flex items-center gap-3 mb-6">
              <span className="text-xs tracking-[0.22em] uppercase font-semibold text-terracotta">{t("The Problem", "समस्या")}</span>
            </Reveal>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] text-ink">
                {t("India's landfills are odour-emitting liabilities.", "भारत के लैंडफिल गंध फैलाने वाली देनदारियाँ हैं।")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate max-w-md">
                {t(
                  "Legacy sites decompose slowly, leak methane and leachate, and choke the communities around them. Engineering alone cannot bring these grounds back to life — biology can.",
                  "पुराने स्थल धीरे-धीरे अपघटित होते हैं, मीथेन व लीचेट रिसाव करते हैं और आसपास के समुदायों का दम घोंटते हैं। अकेले इंजीनियरिंग इन ज़मीनों को जीवित नहीं कर सकती — जीव विज्ञान कर सकता है।"
                )}
              </p>
            </Reveal>

            <ul className="mt-10 divide-y divide-stone/20 border-y border-stone/20">
              {problems.map((p, i) => (
                <Reveal as="li" key={p.en} delay={i * 0.06} className="flex items-center gap-5 py-4">
                  <span className="font-serif italic text-stone text-xl w-8">0{i + 1}</span>
                  <span className="text-base md:text-lg text-ink">{t(p.en, p.hi)}</span>
                </Reveal>
              ))}
            </ul>
          </div>

          <div ref={ref} className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] md:aspect-[16/12] border border-stone/20">
              <motion.img
                style={{ y }}
                src={PROBLEM_IMG}
                alt="Large pile of waste on a landfill site"
                className="w-full h-[115%] object-cover -mt-[7%]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
